import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Diamond, Home, CreditCard, HeadphonesIcon, Settings, Menu, X, Copy, Check, RefreshCw, Users, TrendingUp, DollarSign, Clock, LogOut, Eye, EyeOff, Mail } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { db } from "./firebase";
import { doc, onSnapshot, collection, getDocs } from "firebase/firestore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Lux
const LUX_LOGO = "https://customer-assets.emergentagent.com/job_admin-connect-10/artifacts/7e0pq0cw_file_00000000525c6230a5bcf0c181f5f5bb.png";

// ===================== HEADER COMPONENT =====================
const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/recarregar", label: "Recarregar", icon: CreditCard },
    { path: "/suporte", label: "Suporte", icon: HeadphonesIcon },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-purple-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LUX_LOGO} alt="Lux" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`nav-link flex items-center gap-2 ${location.pathname === item.path ? 'active' : ''}`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`nav-link flex items-center gap-2 ml-4 ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
            >
              <Settings size={18} />
              Admin
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-400 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-purple-500/20">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`nav-link flex items-center gap-2 ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <item.icon size={18} />
                  {item.label}
                </Link>
              ))}
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className={`nav-link flex items-center gap-2 ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
              >
                <Settings size={18} />
                Admin
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// ===================== HOME PAGE =====================
const HomePage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setSettings(response.data);
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen pt-20 hero-bg">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="lux-gradient-text">Recarregue seus</span>
              <br />
              <span className="text-white">Diamantes Lux</span>
            </h1>
            <p className="text-gray-400 text-lg sm:text-xl mb-8 max-w-lg mx-auto md:mx-0">
              Recarregue seus diamantes Lux via PIX em segundos! Escolha a quantidade e receba automaticamente no app.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Button
                onClick={() => navigate('/recarregar')}
                className="btn-lux-gold text-lg px-8 py-6"
                data-testid="cta-recarregar"
              >
                <Diamond className="mr-2 diamond-shine" size={24} />
                Recarregar Agora
              </Button>
              <Button
                onClick={() => navigate('/suporte')}
                variant="outline"
                className="border-purple-500/50 hover:bg-purple-500/10 text-lg px-8 py-6"
                data-testid="cta-suporte"
              >
                <HeadphonesIcon className="mr-2" size={20} />
                Suporte
              </Button>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-yellow-500/30 blur-3xl rounded-full"></div>
              <img
                src={LUX_LOGO}
                alt="Lux App"
                className="relative w-64 sm:w-80 lg:w-96 float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
          <span className="lux-gradient-text">Por que recarregar conosco?</span>
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="glass-card card-hover border-purple-500/20">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <Clock className="text-purple-400" size={24} />
              </div>
              <CardTitle className="text-white">Crédito Instantâneo</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">Seus diamantes são creditados automaticamente assim que o pagamento é confirmado.</p>
            </CardContent>
          </Card>

          <Card className="glass-card card-hover border-purple-500/20">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                <DollarSign className="text-yellow-400" size={24} />
              </div>
              <CardTitle className="text-white">PIX Seguro</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">Pagamento via PIX com QR Code dinâmico. Rápido, seguro e sem taxas extras.</p>
            </CardContent>
          </Card>

          <Card className="glass-card card-hover border-purple-500/20 sm:col-span-2 lg:col-span-1">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mb-4">
                <Diamond className="text-cyan-400 diamond-shine" size={24} />
              </div>
              <CardTitle className="text-white">Melhores Preços</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">
                {settings ? `A partir de R$ ${settings.price_per_1000_diamonds.toFixed(2)} por 1.000 diamantes!` : 'Carregando preços...'}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mb-8">
        <Card className="glass-card-gold border-yellow-500/30 p-8 text-center">
          <CardContent className="pt-6">
            <Diamond className="w-16 h-16 mx-auto text-yellow-400 diamond-shine mb-6" />
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Viva o Brilho da Sua Live
            </h3>
            <p className="text-gray-300 mb-6">
              Envie presentes, apoie seus streamers favoritos e brilhe nas lives do Lux!
            </p>
            <Button
              onClick={() => navigate('/recarregar')}
              className="btn-lux-gold"
              data-testid="cta-bottom-recarregar"
            >
              Começar Recarga
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

// ===================== RECHARGE PAGE =====================
const RechargePage = () => {
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [formData, setFormData] = useState({
    diamonds_quantity: "",
    full_name: "",
    cpf: "",
    email: "",
    lux_user_id: ""
  });
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [errors, setErrors] = useState({});
  const [userLookup, setUserLookup] = useState({ query: "", status: "idle", user: null });
  const lookupTimerRef = useRef(null);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setSettings(response.data);
      } catch (error) {
        toast.error("Erro ao carregar configurações");
      }
    };
    fetchSettings();
  }, []);

  // CPF mask
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cpf') {
      setFormData(prev => ({ ...prev, [name]: formatCPF(value) }));
    } else if (name === 'diamonds_quantity') {
      const numValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numValue }));

      // Calculate amount
      if (numValue && settings) {
        const amount = (parseInt(numValue) / 1000) * settings.price_per_1000_diamonds;
        setCalculatedAmount(amount.toFixed(2));
      } else {
        setCalculatedAmount(null);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.diamonds_quantity || parseInt(formData.diamonds_quantity) < settings?.min_diamonds) {
      newErrors.diamonds_quantity = `Mínimo de ${settings?.min_diamonds} diamantes`;
    }
    if (parseInt(formData.diamonds_quantity) > settings?.max_diamonds) {
      newErrors.diamonds_quantity = `Máximo de ${settings?.max_diamonds} diamantes`;
    }
    if (!formData.full_name || formData.full_name.length < 3) {
      newErrors.full_name = "Nome deve ter pelo menos 3 caracteres";
    }
    if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) {
      newErrors.cpf = "CPF inválido";
    }
    if (userLookup.status !== "found" || !formData.lux_user_id) {
      newErrors.lux_user_id = "Encontre um usuário Lux válido antes de continuar";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Preencha todos os campos corretamente");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/recharge`, {
        ...formData,
        diamonds_quantity: parseInt(formData.diamonds_quantity),
        cpf: formData.cpf.replace(/\D/g, '')
      });

      setTransaction(response.data);
      setStep(2);
      toast.success("PIX gerado com sucesso!");

      // Document is created by the backend (avoids Firestore client permission errors)
      console.log("[diamonds_recharge] Recharge successful. Document created by server.", {
        transaction_id: response.data.id,
        txid: response.data.txid,
        email: formData.email,
        diamonds_amount: parseInt(formData.diamonds_quantity),
        status: "pending"
      });
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao gerar PIX";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = async () => {
    try {
      await navigator.clipboard.writeText(transaction.pix_code);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Erro ao copiar");
    }
  };

  const checkPaymentStatus = useCallback(async () => {
    if (!transaction) return;

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/recharge/${transaction.id}/status`);

      if (response.data.status === 'confirmed') {
        setTransaction(prev => ({ ...prev, status: 'confirmed' }));
        setStep(3);
        toast.success("Pagamento confirmado! Diamantes creditados!");
      }
    } catch (error) {
      // Silencia erros intermitentes para não incomodar o usuário
      console.error("Erro ao verificar status automaticamente:", error);
    } finally {
      setCheckingStatus(false);
    }
  }, [transaction, API]);

  // Real-time updates via Firestore
  useEffect(() => {
    if (step !== 2 || !transaction) return;

    // Clean up any existing listener before creating a new one
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const txDocRef = doc(db, "transactions", transaction.id);
    const unsubscribe = onSnapshot(txDocRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      if (data.status === "confirmed") {
        setTransaction(prev => ({ ...prev, status: "confirmed" }));
        setStep(3);
        toast.success("Pagamento confirmado! Diamantes creditados!");

        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      }
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [step, transaction]);

  // Simulate payment (for testing)
  const simulatePayment = async () => {
    if (!transaction) return;

    setCheckingStatus(true);
    try {
      await axios.post(`${API}/webhook/pix/simulate/${transaction.id}`);
      setTransaction(prev => ({ ...prev, status: 'confirmed' }));
      setStep(3);
      toast.success("Pagamento confirmado! Diamantes creditados!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao simular pagamento");
    } finally {
      setCheckingStatus(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setTransaction(null);
    setFormData({
      diamonds_quantity: "",
      full_name: "",
      cpf: "",
      email: "",
      lux_user_id: ""
    });
    setCalculatedAmount(null);
    setErrors({});
    setUserLookup({ query: "", status: "idle", user: null });
  };

  const handleUserLookupChange = (e) => {
    const query = e.target.value;
    setUserLookup({ query, status: "idle", user: null });
    setFormData(prev => ({ ...prev, email: "", lux_user_id: "" }));
    if (errors.lux_user_id) setErrors(prev => ({ ...prev, lux_user_id: null }));

    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    if (!query.trim()) return;

    setUserLookup(prev => ({ ...prev, status: "loading" }));
    lookupTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/user/lookup?q=${encodeURIComponent(query.trim())}`);
        const user = res.data;
        setUserLookup({ query, status: "found", user });
        setFormData(prev => ({ ...prev, email: user.email || "", lux_user_id: user.id || "" }));
      } catch {
        setUserLookup(prev => ({ ...prev, status: "not_found" }));
      }
    }, 600);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-500'
                }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-purple-500' : 'bg-gray-800'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Form */}
        {step === 1 && (
          <Card className="glass-card border-purple-500/20" data-testid="recharge-form-card">
            <CardHeader className="text-center">
              <Diamond className="w-12 h-12 mx-auto text-purple-400 diamond-shine mb-4" />
              <CardTitle className="text-2xl text-white">Recarga de Diamantes</CardTitle>
              <CardDescription className="text-gray-400">
                Preencha os dados abaixo para gerar seu PIX
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Diamonds quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quantidade de Diamantes
                  </label>
                  <div className="relative">
                    <Diamond className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" size={20} />
                    <Input
                      type="text"
                      name="diamonds_quantity"
                      value={formData.diamonds_quantity}
                      onChange={handleInputChange}
                      placeholder={`Mínimo ${settings?.min_diamonds || 100}`}
                      className="input-lux pl-10"
                      data-testid="input-diamonds"
                    />
                  </div>
                  {errors.diamonds_quantity && (
                    <p className="text-red-400 text-sm mt-1">{errors.diamonds_quantity}</p>
                  )}
                  {calculatedAmount && (
                    <p className="text-yellow-400 text-sm mt-2 font-medium">
                      Valor: R$ {calculatedAmount}
                    </p>
                  )}
                </div>

                {/* Full name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome Completo
                  </label>
                  <Input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Seu nome completo"
                    className="input-lux"
                    data-testid="input-name"
                  />
                  {errors.full_name && (
                    <p className="text-red-400 text-sm mt-1">{errors.full_name}</p>
                  )}
                </div>

                {/* CPF */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    CPF
                  </label>
                  <Input
                    type="text"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleInputChange}
                    placeholder="000.000.000-00"
                    className="input-lux"
                    data-testid="input-cpf"
                  />
                  {errors.cpf && (
                    <p className="text-red-400 text-sm mt-1">{errors.cpf}</p>
                  )}
                </div>

                {/* Lux User ID / Email Lookup */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    E-mail ou ID do usuário Lux
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={userLookup.query}
                      onChange={handleUserLookupChange}
                      placeholder="email@exemplo.com ou ID do usuário"
                      className={`input-lux pr-10 ${
                        userLookup.status === "found" ? "border-green-500/60" :
                        userLookup.status === "not_found" ? "border-red-500/60" : ""
                      }`}
                      data-testid="input-user-lookup"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {userLookup.status === "loading" && (
                        <RefreshCw size={16} className="text-gray-400 animate-spin" />
                      )}
                      {userLookup.status === "found" && (
                        <Check size={16} className="text-green-400" />
                      )}
                    </div>
                  </div>
                  {/* {userLookup.status === "found" && userLookup.user && (
                    <p className="text-green-400 text-sm mt-1">
                      ✓ {userLookup.user.displayName || userLookup.user.email}
                    </p>
                  )} */}
                  {userLookup.status === "not_found" && (
                    <p className="text-red-400 text-sm mt-1">Usuário não encontrado</p>
                  )}
                  {errors.lux_user_id && userLookup.status !== "found" && (
                    <p className="text-red-400 text-sm mt-1">{errors.lux_user_id}</p>
                  )}
                </div>

                <Separator className="my-6 bg-purple-500/20" />

                <Button
                  type="submit"
                  disabled={loading || !settings || userLookup.status !== "found"}
                  className={`w-full py-6 text-lg ${
                    userLookup.status === "found" ? "btn-lux-gold" : "btn-lux"
                  }`}
                  data-testid="btn-generate-pix"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="mr-2 animate-spin" size={20} />
                      Gerando PIX...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2" size={20} />
                      Gerar PIX
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: QR Code */}
        {step === 2 && transaction && (
          <Card className="glass-card border-purple-500/20" data-testid="qrcode-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Pague com PIX</CardTitle>
              <CardDescription className="text-gray-400">
                Escaneie o QR Code ou copie o código PIX
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Amount */}
              <div className="text-center">
                <p className="text-gray-400 text-sm">Valor a pagar</p>
                <p className="text-4xl font-bold lux-gradient-text">
                  R$ {transaction.amount_brl.toFixed(2)}
                </p>
                <p className="text-purple-400 mt-1">
                  {transaction.diamonds_quantity.toLocaleString()} diamantes
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="qr-container pulse-glow">
                  <img
                    src={transaction.pix_qr_base64}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                    data-testid="qrcode-image"
                  />
                </div>
              </div>

              {/* PIX Code */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Código PIX Copia e Cola
                </label>
                <div className="flex gap-2">
                  <textarea
                    value={transaction.pix_code}
                    readOnly
                    rows={3}
                    className="input-lux text-xs font-mono resize-none overflow-x-auto"
                    data-testid="pix-code-input"
                  />
                  <Button
                    onClick={copyPixCode}
                    className={copied ? "bg-green-600" : "btn-lux"}
                    data-testid="btn-copy-pix"
                  >
                    {copied ? <Check size={20} /> : <Copy size={20} />}
                  </Button>
                </div>
              </div>

              {/* Message */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 text-sm text-center">
                  {transaction.message}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={resetForm}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  Cancelar e voltar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <Card className="glass-card-gold border-green-500/30" data-testid="confirmation-card">
            <CardContent className="pt-8 text-center space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-10 h-10 text-green-400" />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Pagamento Confirmado!
                </h2>
                <p className="text-gray-400">
                  Seus diamantes foram creditados automaticamente no app Lux.
                </p>
              </div>

              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                <Diamond className="w-12 h-12 mx-auto text-purple-400 diamond-shine mb-4" />
                <p className="text-3xl font-bold text-white">
                  +{transaction?.diamonds_quantity.toLocaleString()}
                </p>
                <p className="text-purple-400">diamantes creditados</p>
              </div>

              <Button
                onClick={resetForm}
                className="btn-lux-gold w-full"
                data-testid="btn-new-recharge"
              >
                <Diamond className="mr-2" size={20} />
                Nova Recarga
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ===================== SUPPORT PAGE =====================
const SupportPage = () => {
  const faqs = [
    {
      question: "Como encontro meu ID no app Lux?",
      answer: "Abra o app Lux, vá até seu perfil e clique no ícone de configurações. Seu ID estará visível na seção 'Informações da Conta'."
    },
    {
      question: "Quanto tempo demora para os diamantes serem creditados?",
      answer: "Os diamantes são creditados automaticamente assim que o pagamento PIX é confirmado, geralmente em poucos segundos."
    },
    {
      question: "O que acontece se eu informar o ID errado?",
      answer: "Os diamantes serão creditados no ID informado. Por isso, sempre verifique seu ID antes de realizar a recarga. Não é possível estornar ou transferir diamantes após o crédito."
    },
    {
      question: "Posso pagar com cartão de crédito?",
      answer: "No momento, aceitamos apenas pagamentos via PIX para garantir crédito instantâneo e sem taxas adicionais."
    },
    {
      question: "Qual o valor mínimo de recarga?",
      answer: "O valor mínimo depende das configurações do sistema. Consulte a página de recarga para ver os limites atuais."
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <HeadphonesIcon className="w-16 h-16 mx-auto text-purple-400 mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Central de Suporte
          </h1>
          <p className="text-gray-400">
            Encontre respostas para suas dúvidas ou entre em contato conosco
          </p>
        </div>

        {/* FAQs */}
        <Card className="glass-card border-purple-500/20 mb-8" data-testid="faq-section">
          <CardHeader>
            <CardTitle className="text-white">Perguntas Frequentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-purple-500/10 pb-4 last:border-0">
                <h3 className="font-medium text-white mb-2">{faq.question}</h3>
                <p className="text-gray-400 text-sm">{faq.answer}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="glass-card-gold border-yellow-500/30" data-testid="contact-section">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold text-white mb-4">
              Não encontrou sua resposta?
            </h3>
            <p className="text-gray-400 mb-4">
              Entre em contato conosco por email e responderemos o mais rápido possível.
            </p>
            <div className="flex items-center justify-center gap-2 mb-6 bg-purple-500/10 border border-purple-500/20 rounded-lg px-4 py-3">
              <Mail size={18} className="text-purple-400" />
              <span className="text-white font-medium">luxliveapp@gmail.com</span>
            </div>
            <Button
              className="btn-lux-gold"
              onClick={() => window.open('mailto:luxliveapp@gmail.com', '_blank')}
            >
              <Mail className="mr-2" size={20} />
              Enviar E-mail
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ===================== ADMIN LOGIN =====================
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        localStorage.setItem('admin_logged_in', 'true');
        onLogin(true);
        toast.success("Login realizado com sucesso!");
      }
    } catch (error) {
      toast.error("Credenciais inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <Card className="glass-card border-purple-500/20 w-full max-w-md" data-testid="admin-login-card">
        <CardHeader className="text-center">
          <Settings className="w-12 h-12 mx-auto text-purple-400 mb-4" />
          <CardTitle className="text-2xl text-white">Painel Administrativo</CardTitle>
          <CardDescription className="text-gray-400">
            Faça login para acessar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                E-mail do Administrador
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin@gmail.com"
                className="input-lux"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-lux pr-10"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-lux w-full"
              data-testid="admin-login-btn"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ===================== ADMIN DASHBOARD =====================
const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalTransactionsCount, setTotalTransactionsCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const applyListAndStats = useCallback((list) => {
    setTransactions(list);
    setTotalTransactionsCount(list.length);
    setPage(1);
    // Only completed diamonds_recharge: paid, confirmed, or completed (case-insensitive)
    const isCompleted = (t) => {
      const s = (t.status || "").toString().toLowerCase();
      return s === "paid" ;
    };
    const completedOnly = list.filter(isCompleted);
    const totalDiamondsSold = completedOnly.reduce((sum, t) => sum + (t.diamonds_quantity || 0), 0);
    const totalRevenueBrl = completedOnly.reduce((sum, t) => sum + (t.amount_brl || 0), 0);
    setStats({
      total_transactions: list.length,
      confirmed_transactions: completedOnly.length,
      pending_transactions: list.filter((t) => (t.status || "").toString().toLowerCase() === "pending").length,
      total_diamonds_sold: totalDiamondsSold,
      total_revenue_brl: Math.round(totalRevenueBrl * 100) / 100
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (localStorage.getItem('admin_logged_in') !== 'true') {
        onLogout();
        return;
      }

      const settingsRes = await axios.get(`${API}/admin/settings`);
      setSettings(settingsRes.data);

      let list = [];

      try {
        const collRef = collection(db, "diamonds_recharge");
        const snapshot = await getDocs(collRef);
        list = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            transaction_id: data.transaction_id ?? null,
            full_name: data.full_name ?? null,
            cpf: data.cpf ?? null,
            email: data.email ?? null,
            lux_user_id: data.lux_user_id ?? null,
            diamonds_quantity: data.diamonds_amount ?? data.amount ?? 0,
            amount_brl: data.amount_brl ?? data.brl ?? 0,
            status: data.status ?? "pending",
            created_at: data.created_at?.toDate?.()?.toISOString?.() ?? data.created_at ?? null
          };
        });
        list.sort((a, b) => {
          if (!a.created_at && !b.created_at) return 0;
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        console.log("[diamonds_recharge] direct Firestore read:", list.length, "docs, paid:", list.filter(t => t.status === "paid").length);
      } catch (firestoreErr) {
        console.warn("[diamonds_recharge] Firestore blocked (" + (firestoreErr?.message || firestoreErr) + "), using backend Admin SDK");
        const transactionsRes = await axios.get(`${API}/admin/transactions?limit=10000`);
        const raw = transactionsRes.data.transactions || [];
        list = raw.map((t) => ({
          id: t.id,
          transaction_id: t.transaction_id ?? null,
          full_name: t.full_name ?? null,
          cpf: t.cpf ?? null,
          email: t.email ?? null,
          lux_user_id: t.lux_user_id ?? null,
          diamonds_quantity: t.diamonds_quantity ?? 0,
          amount_brl: t.amount_brl ?? 0,
          status: t.status ?? "pending",
          created_at: t.created_at ?? null
        }));
        console.log("[diamonds_recharge] backend Admin SDK read:", list.length, "docs, paid:", list.filter(t => t.status === "paid").length);
      }

      applyListAndStats(list);
    } catch (error) {
      console.error("Admin fetch error:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [applyListAndStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSettings = async (updates) => {
    try {
      await axios.put(`${API}/admin/settings`, updates);
      toast.success("Configurações atualizadas!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar configurações");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    onLogout();
    toast.success("Logout realizado");
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Admin Header */}
      <div className="bg-purple-900/20 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Settings className="text-purple-400" size={24} />
              <h1 className="text-xl font-bold text-white">Painel Administrativo</h1>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="mr-2" size={18} />
              Sair
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            {["dashboard", "settings", "transactions"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === tab
                  ? "bg-purple-500 text-white"
                  : "text-gray-400 hover:text-white hover:bg-purple-500/20"
                  }`}
                data-testid={`admin-tab-${tab}`}
              >
                {tab === "dashboard" && "Dashboard"}
                {tab === "settings" && "Configurações"}
                {tab === "transactions" && "Transações"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6" data-testid="admin-dashboard">
            {/* Stats Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-card border-purple-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Transações</p>
                      <p className="text-3xl font-bold text-white">{stats.total_transactions}</p>
                    </div>
                    <CreditCard className="text-purple-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-green-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Confirmadas</p>
                      <p className="text-3xl font-bold text-green-400">{stats.confirmed_transactions}</p>
                    </div>
                    <Check className="text-green-400" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-yellow-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Diamantes Vendidos</p>
                      <p className="text-3xl font-bold text-yellow-400">
                        {stats.total_diamonds_sold.toLocaleString()}
                      </p>
                    </div>
                    <Diamond className="text-yellow-400 diamond-shine" size={32} />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-cyan-500/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Receita Total</p>
                      <p className="text-3xl font-bold text-cyan-400">
                        R$ {stats.total_revenue_brl.toFixed(2)}
                      </p>
                    </div>
                    <TrendingUp className="text-cyan-400" size={32} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card className="glass-card border-purple-500/20">
              <CardHeader>
                <CardTitle className="text-white">Transações Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="table-lux">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>Diamantes</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 5).map((tx) => (
                        <tr key={tx.id}>
                          <td className="text-white">{tx.lux_user_id}</td>
                          <td className="text-purple-400">{tx.diamonds_quantity.toLocaleString()}</td>
                          <td className="text-yellow-400">R$ {tx.amount_brl.toFixed(2)}</td>
                          <td>
                            <Badge className={`status-${tx.status}`}>
                              {tx.status}
                            </Badge>
                          </td>
                          <td className="text-gray-400 text-sm">
                            {tx.created_at ? new Date(tx.created_at).toLocaleString('pt-BR') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && settings && (
          <Card className="glass-card border-purple-500/20 max-w-2xl" data-testid="admin-settings">
            <CardHeader>
              <CardTitle className="text-white">Configurações do Sistema</CardTitle>
              <CardDescription className="text-gray-400">
                Ajuste os parâmetros de recarga
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preço por 1.000 Diamantes (R$)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  defaultValue={settings.price_per_1000_diamonds}
                  onBlur={(e) => updateSettings({ price_per_1000_diamonds: parseFloat(e.target.value) })}
                  className="input-lux"
                  data-testid="settings-price"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mínimo de Diamantes
                  </label>
                  <Input
                    type="number"
                    defaultValue={settings.min_diamonds}
                    onBlur={(e) => updateSettings({ min_diamonds: parseInt(e.target.value) })}
                    className="input-lux"
                    data-testid="settings-min"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Máximo de Diamantes
                  </label>
                  <Input
                    type="number"
                    defaultValue={settings.max_diamonds}
                    onBlur={(e) => updateSettings({ max_diamonds: parseInt(e.target.value) })}
                    className="input-lux"
                    data-testid="settings-max"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expiração do PIX (minutos)
                </label>
                <Input
                  type="number"
                  defaultValue={settings.pix_expiration_minutes}
                  onBlur={(e) => updateSettings({ pix_expiration_minutes: parseInt(e.target.value) })}
                  className="input-lux"
                  data-testid="settings-expiration"
                />
              </div>

              <Separator className="bg-purple-500/20" />

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-medium mb-2">Integração Efibank</h4>
                <p className="text-gray-400 text-sm">
                  A integração com a Efibank está preparada. Configure as credenciais no arquivo .env do backend para ativar PIX real.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <Card className="glass-card border-purple-500/20" data-testid="admin-transactions">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Todas as Transações</CardTitle>
                <CardDescription className="text-gray-400">
                  {totalTransactionsCount} transações encontradas
                </CardDescription>
              </div>
              <Button onClick={() => fetchData()} variant="outline" className="border-purple-500/50">
                <RefreshCw className="mr-2" size={18} />
                Atualizar
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="table-lux">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>CPF</th>
                      <th>Email</th>
                      <th>Lux ID</th>
                      <th>Diamantes</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice((page - 1) * pageSize, page * pageSize).map((tx) => (
                      <tr key={tx.id}>
                        <td className="font-mono text-xs text-gray-400">{tx.id.slice(0, 8)}...</td>
                        <td className="text-white">{tx.full_name}</td>
                        <td className="font-mono text-gray-400">{tx.cpf}</td>
                        <td className="text-gray-400">{tx.email}</td>
                        <td className="text-purple-400">{tx.lux_user_id}</td>
                        <td className="text-yellow-400">{tx.diamonds_quantity.toLocaleString()}</td>
                        <td className="text-cyan-400">R$ {tx.amount_brl.toFixed(2)}</td>
                        <td>
                          <Badge className={`status-${tx.status}`}>
                            {tx.status}
                          </Badge>
                        </td>
                        <td className="text-gray-400 text-sm">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString('pt-BR') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Paginação */}
              {totalTransactionsCount > pageSize && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-gray-400 text-sm">
                    Página {page} de {Math.ceil(totalTransactionsCount / pageSize)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= Math.ceil(totalTransactionsCount / pageSize)}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ===================== ADMIN PAGE =====================
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('admin_logged_in') === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  if (!isLoggedIn) {
    return <AdminLogin onLogin={setIsLoggedIn} />;
  }

  return <AdminDashboard onLogout={() => setIsLoggedIn(false)} />;
};

// ===================== FOOTER =====================
const Footer = () => {
  return (
    <footer className="bg-black/50 border-t border-purple-500/20 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LUX_LOGO} alt="Lux" className="h-8 w-auto" />
            <span className="text-gray-400 text-sm">
              © 2024 Lux App. Todos os direitos reservados.
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="#" className="hover:text-purple-400 transition-colors">Termos de Uso</a>
            <a href="#" className="hover:text-purple-400 transition-colors">Privacidade</a>
            <a href="#" className="hover:text-purple-400 transition-colors">Contato</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ===================== MAIN APP =====================
function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1A1A2E',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            color: '#F9FAFB',
          },
        }}
      />
      <BrowserRouter>
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/recarregar" element={<RechargePage />} />
            <Route path="/suporte" element={<SupportPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
